import {useSyncExternalStore} from 'react';
import ProgressBar from '../components/feedback/ProgressBar';
import Spinner from '../components/feedback/Spinner';

// A blocking overlay for the few operations that must hold the screen (export downloads,
// permanent deletes). Module store, so it can be driven from anywhere:
//   const done = busy.show({message: 'Preparing part 1 of 8', onCancel});  …  busy.update({progress: 0.4}); done();
// Saving a tell deliberately does not use this: the card appears and the photos follow.

export interface BusyState {
    message: string;
    detail?: string;
    /** 0..1; omit for indeterminate. */
    progress?: number;
    onCancel?: () => void;
}

let state: BusyState | null = null;
const listeners = new Set<() => void>();
const emit = () => listeners.forEach(l => l());

export const busy = {
    show(next: BusyState): () => void {
        state = next;
        emit();
        return () => {
            if (state === next || state?.message === next.message) busy.hide();
        };
    },
    update(patch: Partial<BusyState>) {
        if (!state) return;
        state = {...state, ...patch};
        emit();
    },
    hide() {
        state = null;
        emit();
    },
    /** Wraps a promise: shows while pending, hides after. */
    async during<T>(opts: BusyState, work: (update: (patch: Partial<BusyState>) => void) => Promise<T>): Promise<T> {
        const done = busy.show(opts);
        try {
            return await work(busy.update);
        } finally {
            done();
        }
    }
};

const subscribe = (l: () => void) => {
    listeners.add(l);
    return () => listeners.delete(l);
};

export const useBusy = () => useSyncExternalStore(subscribe, () => state, () => state);

export function BusyOverlay() {
    const current = useBusy();
    if (!current) return null;
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-6" role="dialog" aria-modal="true" aria-live="polite">
            <div className="scrim absolute inset-0" />
            <div className="settle-in relative flex w-full max-w-[360px] flex-col items-center gap-4 rounded-xl bg-ground p-6 text-center shadow-float">
                <Spinner />
                <div>
                    <div className="font-display text-lg font-[650] leading-tight">{current.message}</div>
                    {current.detail && <div className="meta mt-1">{current.detail}</div>}
                </div>
                {typeof current.progress === 'number' && <ProgressBar value={current.progress} />}
                {current.onCancel && (
                    <button type="button" className="btn btn-secondary btn-sm" onClick={current.onCancel}>
                        Cancel
                    </button>
                )}
            </div>
        </div>
    );
}
